import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Layers3 } from 'lucide-react';
import MapaVivo from './MapaVivo';
import { URL_SOLICITAR_ACCESO, URL_MUNICIPIO_EJEMPLO } from '../../lib/urbanismoBase';

const URL_ALTA_TERRITORIO = 'https://aplicacion.geovis.es/auth/signup?source=landing_home';
const ROTACION_MS = 8000;

// Las dos puertas de la plataforma. Urbanismo va primero; Territorio con el mismo peso.
const VISTAS = {
  urbanismo: {
    icono: Building2,
    nombre: 'Urbanismo',
    pregunta: 'Qué se puede construir',
    texto: 'Qué se puede construir en cada parcela, con el artículo y la página del plan.',
    principal: { texto: 'Solicitar acceso', to: URL_SOLICITAR_ACCESO },
    secundario: { texto: 'Ver un municipio de ejemplo', href: URL_MUNICIPIO_EJEMPLO },
  },
  territorio: {
    icono: Layers3,
    nombre: 'Territorio',
    pregunta: 'Qué afecta al terreno',
    texto: 'Todo lo que afecta al terreno: más de 80 capas oficiales en un mapa, en 15 países.',
    principal: { texto: 'Empezar gratis', href: URL_ALTA_TERRITORIO },
    secundario: { texto: 'Ver GeoVis Territorio', to: '/territorio' },
  },
};
const ORDEN = ['urbanismo', 'territorio'];

/**
 * Héroe de la home: GeoVis como plataforma con dos puertas. Las pestañas alternan
 * solas cada 8 s (con barra de progreso) hasta que el usuario elige una; con el
 * ratón sobre el mapa o con reduced-motion no rotan.
 */
export default function HeroPlataforma() {
  const [modo, setModo] = useState('urbanismo');
  const [manual, setManual] = useState(false);
  const [pausa, setPausa] = useState(false);
  const reducido = useRef(false);

  useEffect(() => {
    reducido.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (manual || pausa || reducido.current) return undefined;
    const t = setTimeout(() => setModo((m) => (m === 'urbanismo' ? 'territorio' : 'urbanismo')), ROTACION_MS);
    return () => clearTimeout(t);
  }, [modo, manual, pausa]);

  const elegir = (m) => {
    setManual(true);
    setModo(m);
  };
  const alTeclado = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const sig = ORDEN[(ORDEN.indexOf(modo) + 1) % ORDEN.length];
    elegir(sig);
    document.getElementById(`pestana-${sig}`)?.focus();
  };

  const v = VISTAS[modo];
  const rotando = !manual && !pausa;

  return (
    <section className="mx-auto max-w-7xl px-6 pt-32 pb-16 sm:pt-36 lg:pb-24">
      <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-6">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">GeoVis · plataforma de suelo</p>
          <h1
            data-testid="hero-heading"
            className="mt-5 text-[2.6rem] leading-[1.04] tracking-tight text-foreground sm:text-6xl lg:text-[4rem]"
          >
            Decide sobre el suelo con el dato oficial <em className="text-[oklch(var(--primary-text))]">y su fuente.</em>
          </h1>
          <div role="tablist" aria-label="Productos de GeoVis" className="mt-9 grid grid-cols-2 gap-2" onKeyDown={alTeclado}>
            {ORDEN.map((id) => {
              const { icono: Icono, nombre, pregunta } = VISTAS[id];
              const activa = id === modo;
              return (
                <button
                  key={id}
                  id={`pestana-${id}`}
                  type="button"
                  role="tab"
                  aria-selected={activa}
                  aria-controls="panel-producto"
                  tabIndex={activa ? 0 : -1}
                  onClick={() => elegir(id)}
                  className={`${id === 'urbanismo' ? 'tinte-urbanismo' : 'tinte-territorio'} relative overflow-hidden rounded-lg border px-4 py-3 text-left transition-colors duration-200 ${
                    activa ? 'border-acento/50 bg-card shadow-sm' : 'border-border bg-card/40 hover:bg-card/80'
                  }`}
                >
                  <span className="flex items-center gap-2 text-base font-semibold text-foreground">
                    <Icono className={`h-4 w-4 ${activa ? 'text-acento' : 'text-muted-foreground'}`} aria-hidden="true" />
                    {nombre}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{pregunta}</span>
                  {activa && (
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-acento/25" aria-hidden="true">
                      {rotando && <span key={modo} className="pestana-progreso block h-full bg-acento" style={{ '--dur': `${ROTACION_MS}ms` }} />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div id="panel-producto" role="tabpanel" aria-labelledby={`pestana-${modo}`} className={modo === 'urbanismo' ? 'tinte-urbanismo' : 'tinte-territorio'}>
            <p key={`t-${modo}`} className="anim-entra mt-5 max-w-xl text-lg leading-relaxed text-foreground/85">{v.texto}</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              {(() => {
                const clase =
                  'btn-vivo inline-flex h-12 items-center justify-center rounded-lg bg-primary px-6 text-base font-medium text-primary-foreground transition-colors duration-200 hover:bg-primary/90';
                const contenido = (
                  <>
                    {v.principal.texto}
                    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                  </>
                );
                return v.principal.to ? (
                  <Link to={v.principal.to} className={clase}>{contenido}</Link>
                ) : (
                  <a href={v.principal.href} target="_blank" rel="noopener noreferrer" className={clase}>{contenido}</a>
                );
              })()}
              {v.secundario.href ? (
                <a
                  href={v.secundario.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-12 items-center justify-center rounded-lg border border-border bg-card/60 px-6 text-base font-medium text-foreground transition-colors duration-200 hover:bg-accent"
                >
                  {v.secundario.texto}
                </a>
              ) : (
                <Link
                  to={v.secundario.to}
                  className="inline-flex h-12 items-center justify-center rounded-lg border border-border bg-card/60 px-6 text-base font-medium text-foreground transition-colors duration-200 hover:bg-accent"
                >
                  {v.secundario.texto}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Con el ratón sobre el mapa se detiene la rotación para poder mirarlo. */}
        <div className="lg:col-span-6" onMouseEnter={() => setPausa(true)} onMouseLeave={() => setPausa(false)}>
          <MapaVivo modo={modo} />
        </div>
      </div>
    </section>
  );
}
